import { Link } from 'react-router-dom';
import Button from '@/components/common/Button';
import AdvisorImage from '@/components/common/AdvisorImage';
import StaticPageLayout from '@/components/layout/StaticPageLayout';
import distressedImg from '@/images/distressed.png';
import advisor1Img from '@/images/clay.png';
import advisor2Img from '@/images/mark_lind.png';
import advisor3Img from '@/images/jasper.png';

export default function BlogPostShowFeelLikeSomething() {
  return (
    <StaticPageLayout>
      {/* ============ HERO / HEADER ============ */}
      <section className="relative z-[6] -mt-[50px] bg-[#111] bg-cover bg-center px-6 pb-[calc(100px+50px)] pt-[calc(100px+50px)] text-center md:-mt-[80px] md:px-10 md:pb-[calc(100px+80px)] md:pt-[calc(100px+80px)] [clip-path:polygon(0_50px,100%_0,100%_calc(100%-50px),0_100%)] md:[clip-path:polygon(0_80px,100%_0,100%_calc(100%-80px),0_100%)]"
      style={{ backgroundImage: `url(${distressedImg})` }}
      >
        <div className="mx-auto max-w-3xl px-6">
          <Link
            to="/blog"
            className="text-sm text-primary-600 font-semibold hover:text-primary-700 transition-colors no-underline"
          >
            ← Back to Blog
          </Link>
          <h1 className="mt-6 font-heading text-[36px] uppercase tracking-[2px] text-white md:text-[60px] leading-[1]">
            YOUR SHOW HAS TO FEEL LIKE SOMETHING
          </h1>
          <p className="mt-4 text-lg text-neutral-500">
            July 2026 · 7 min read
          </p>
        </div>
      </section>

      {/* ============ ARTICLE BODY ============ */}
      <section className="relative -mt-[80px] bg-neutral-200 px-6 pb-[calc(100px+80px)] pt-[calc(100px+80px)] [clip-path:polygon(0_80px,100%_0,100%_calc(100%-80px),0_100%)]">
        <div className="mx-auto max-w-3xl">
          <article className="max-w-none">

            {/* ADVISOR NOTE */}
            <p className="text-sm italic text-neutral-500 leading-relaxed">
              Every BandJerry article is shaped by our Band of Advisors: musicians, promoters, bookers, tour managers, merch pros, and scene veterans who have spent years figuring out what actually helps bands grow. We pull from hours of interviews with them and turn their hard-earned expertise into practical insight you can use to make smarter moves.
            </p>

            {/* INTRO */}
            <p className="mt-8 text-lg text-neutral-700 leading-relaxed">
              A lot of smaller bands treat shows like calendar listings.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              Date. Venue. Time. Flyer. Done.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              But at this stage you're not just raising awareness, you're fighting inertia. People have endless reasons not to go out. They are tired. Shows are expensive. Drinks are expensive. Parking sucks. They already have a couch, phone, and 9,000 other entertainment options within reach.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              If you're going to overcome all that, the show has to feel like something bigger than "our band is playing."
            </p>

            <div className="mt-8 flex items-center gap-4">
              <AdvisorImage src={advisor1Img} alt="C.F., promoter" size={100} />
              <blockquote className="border-l-4 border-primary-600 bg-neutral-50  p-6 italic text-neutral-700">
                "When you're building a local show, you have to make it a party."
                <span className="mt-2 block text-sm not-italic text-neutral-500">— C.F., promoter</span>
              </blockquote>
            </div>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              That doesn't mean balloons and gimmicks. Unless clowncore is your thing, in which case, yeah, it definitely means that.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              For everyone else, it means making the night feel intentional. Putting together a bill that makes sense without being too obvious. Bringing people together in a way that creates a little world just for that one night.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              That might mean pairing with bands your crowd doesn't already know but should. It might mean turning a single release into a listening party, a pizza party, and then a show. Or organizing a pre-show acoustic set in a different location and bringing everyone with you from one place to the next.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed font-semibold text-white">
              Any asshole can book a show. A smart band curates a night out.
            </p>

            <h2 className="mt-16 font-heading text-3xl uppercase text-primary-600">
              DON'T JUST ASK FOR A SHOW
            </h2>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              One of the best pieces of advice we received was about how starter bands should approach venues.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              A lot of bands think the move is to send their stuff to a club and wait. Here's our demo. Here's our link. Here's our EPK. Can we play?
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              That might work once in a while. But most venues and bookers are already drowning in those types of interactions.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              The key to getting in good with clubs isn't asking for a show, but bringing them something they can say yes to.
            </p>

            <div className="mt-8 flex items-center gap-4">
              <AdvisorImage src={advisor2Img} alt="M.L., independent musician / promoter / distro owner" size={100} />
              <blockquote className="border-l-4 border-primary-600 bg-neutral-50  p-6 italic text-neutral-700">
                "You've got to put your own bill together and bring it to them."
                <span className="mt-2 block text-sm not-italic text-neutral-500">— M.L., independent musician / promoter / distro owner</span>
              </blockquote>
            </div>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              That changes the relationship and tips the scales in your favor.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Instead of begging for a slot, you're giving them an opportunity. A chance to work less hard and maybe tap into a new crowd. You're solving a problem instead of creating one and showing them you understand their room, audience, and the fact that the venue isn't a charity; it's a business with its shit to manage.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              It's not just, "Can we play at your club?" It's, "Can my event happen at your place?"
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              That is a completely different ask, and it sweetens the pot considerably if you're a newer band. You are not owed a slot. You are not owed attention. You are trying to make yourself useful to the people who can help you. Get that, and you'll get in front of more people way faster.
            </p>

            <h2 className="mt-16 font-heading text-3xl uppercase text-primary-600">
              BUILD BILLS, NOT JUST SETS
            </h2>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              So how do you put together a good bill? Ask friends and trust your gut.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Not every band needs to sound the same. In fact, it's probably better if they don't. But the night should make sense. There should be some overlap in audience, attitude, scene, or energy. Bonus points if you know and trust the other performers not to show up late, break stuff, and piss off the staff. Never forget: the bill builder is borderline responsible for everything that goes down, good and bad.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              A good bill gives people more reasons to show up, and more reasons to stay. A bad bill makes everyone feel like they accidentally walked into a party they weren't invited to and are desperate to leave.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              When you're putting a show together, think beyond your own 30 minutes. Slot acts in a way that capitalizes on natural, and maybe unrecognized, synergies. It's tempting to put two very similar bands back-to-back, but spacing them out in a way that allows for more overlap between separate fan bases might keep the room fuller for longer. Even in tiny rooms, you need to be cognizant of, if not adhere to, age-old hierarchies around crowd size and "legacy," but finding ways to mix shit up and having it work is the difference between booking bands and building a show. Have fun with it.
            </p>

            <h2 className="mt-16 font-heading text-3xl uppercase text-primary-600">
              DON'T DROP THE FLYER INTO THE ETHER
            </h2>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Outside of door draw and alcohol sales, there's only one other metric venues and promoters care about when working with bands: hustle and a "same team" mentality.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              The bands that grow the fastest are the ones who are easy to root for. They're not just posting once and hoping the internet does the rest. They're working with the venue and promoter, pushing the hell out of the show online and tagging the club and promoter. If you're gonna put in the work, take the credit. Then, at the show, they're thanking the club and crowd, working the merch table, getting along with staff, and acting like the night matters.
            </p>

            <div className="mt-8 flex items-center gap-4">
              <AdvisorImage src={advisor3Img} alt="J.M., venue owner" size={100} />
              <blockquote className="border-l-4 border-primary-600 bg-neutral-50  p-6 italic text-neutral-700">
                "They're not just dropping a flyer into the ether and seeing what happens. They hustle, and they make sure the right people see them doing it."
                <span className="mt-2 block text-sm not-italic text-neutral-500">— J.M., venue owner</span>
              </blockquote>
            </div>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              That sounds obvious because it is. It's almost embarrassingly basic, which is exactly why it is so annoying to clubs when bands don't do it.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Post the show. Tag the venue and the booker. Tag the other bands. Share the flyer, but don't make the flyer do all the work. Talk about why the night is cool. Talk up the other bands. Make the bill feel not just like something you're playing, but something you're proud of.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              And this is important, so it's worth repeating: make sure the right people see you doing the work.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Don't be a try-hard or teacher's pet about it, but make sure your efforts are visible enough that the venue knows you are not sitting around waiting for someone else to bring out the crowd. They'll see it and appreciate it, even if the draw is still smaller than you hoped.
            </p>

            <h2 className="mt-16 font-heading text-3xl uppercase text-primary-600">
              MAKE THE ROOM FEEL WORTH LEAVING THE HOUSE FOR
            </h2>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              The big point is simple: your gig is competing with everything else in a person's life.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Not just other shows, of which there are many, but also work, family, money, weather, weed, and a surprisingly comfortable pair of sweatpants.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              If you want people there, especially those who don't owe you anything, it has to feel like it offers something they can't get at home.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Curate the bill so the room feels alive. Make a release a reason to leave the house. Promote it like it's a fleeting moment instead of yet another obligation.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              The best small shows I've ever been to felt like they mattered, even if for just one night with the other 40 people lucky enough to be there.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed font-semibold text-white">
              Don't think of it as a gig, but a chance to build a night someone wants to talk about after.
            </p>

          </article>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section className="relative z-[6] -mt-[50px] bg-[#111] bg-cover bg-center px-6 pb-[calc(100px+50px)] pt-[calc(100px+50px)] text-center md:-mt-[80px] md:px-10 md:pb-[calc(100px+80px)] md:pt-[calc(100px+80px)] [clip-path:polygon(0_50px,100%_0,100%_calc(100%-50px),0_100%)] md:[clip-path:polygon(0_80px,100%_0,100%_calc(100%-80px),0_100%)]"
      style={{ backgroundImage: `url(${distressedImg})` }}
      >
        <div className="mx-auto max-w-3xl">
          <h2 className="font-heading text-[36px] uppercase tracking-[2px] text-primary-600 md:text-[60px]">
            STOP GUESSING. START BUILDING SHOWS THAT MATTER.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-neutral-600">
            BandJerry helps you find the right venues, build smart bills, and promote shows with a plan. Get personalized, actionable marketing moves tailored to your band's genre, city, and goals.
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-lg font-semibold text-white">
            Your first 3 Moves are free. No credit card required.
          </p>
          <div className="mt-10 flex items-center justify-center">
            <Link to="/signup">
              <Button size="lg">Start Free — Get Your First 3 Moves</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ RELATED CONTENT / ABOUT ============ */}
      <section className="relative -mt-[80px] bg-neutral-200 px-6 pb-32 pt-[calc(100px+80px)] [clip-path:polygon(0_80px,100%_0,100%_100%,0_100%)]">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-heading text-3xl uppercase tracking-[2px] text-primary-600">
            ABOUT BANDJERRY
          </h2>
          <p className="mt-6 text-lg text-neutral-600">
            BandJerry is a marketing tool built specifically for independent musicians. It generates customized, actionable marketing tasks tailored to your band's goals, genre, and location.
          </p>
          <p className="mt-4 text-lg text-neutral-600">
            Instead of generic advice that could apply to anyone, BandJerry gives you specific moves: real venue contacts, pre-written outreach emails, playlist curator leads, social media strategies, and step-by-step plans you can execute in a single sitting.
          </p>
          <div className="mt-8 flex items-center gap-4">
            <Link to="/about">
              <Button variant="secondary">Learn More About Us</Button>
            </Link>
            <Link to="/blog">
              <Button variant="secondary">Back to Blog</Button>
            </Link>
          </div>
        </div>
      </section>
    </StaticPageLayout>
  );
}
